import React, { useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  Alert,
  Badge,
  Table,
  Collapse,
  Progress,
  Spinner
} from 'reactstrap';
import {
  ChevronDown,
  ChevronUp,
  Play,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  Info
} from 'feather-icons-react';

const DataQuality = () => {
  const [diagnostics, setDiagnostics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedAssets, setExpandedAssets] = useState({});

  // API Base URL - use environment variable or fallback to localhost
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const runDiagnostics = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/data-quality-check`);
      const data = await response.json();
      
      if (data.success) {
        setDiagnostics(data.diagnostics);
      } else {
        setError(data.error || 'Failed to run diagnostics');
      }
    } catch (err) {
      console.error('Error running diagnostics:', err);
      setError('Failed to connect to backend service');
    }
    
    setLoading(false);
  };

  const toggleAssetExpansion = (symbol) => {
    setExpandedAssets(prev => ({
      ...prev,
      [symbol]: !prev[symbol]
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'success';
      case 'warning': return 'warning';
      case 'critical': return 'danger';
      default: return 'secondary';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="text-success" size={16} />;
      case 'warning': return <AlertTriangle className="text-warning" size={16} />;
      case 'critical': return <AlertCircle className="text-danger" size={16} />;
      default: return <Info className="text-info" size={16} />;
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  const formatPrice = (price) => {
    return `$${formatNumber(Math.round(price))}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const renderAssetSummary = (asset) => (
    <Card key={asset.symbol} className="mb-3">
      <CardBody>
        <Row className="align-items-center">
          <Col xs="auto">
            {getStatusIcon(asset.status)}
          </Col>
          <Col>
            <h5 className="mb-1">{asset.symbol}</h5>
            <p className="text-muted mb-0">
              {formatNumber(asset.totalRecords)} records • 
              {asset.timeframes.count || 0} timeframes •
              {asset.issues.length} issues
            </p>
          </Col>
          <Col xs="auto">
            <Badge color={getStatusColor(asset.status)} className="me-2">
              {asset.status.toUpperCase()}
            </Badge>
            <Button
              size="sm"
              color="outline-primary"
              onClick={() => toggleAssetExpansion(asset.symbol)}
            >
              {expandedAssets[asset.symbol] ? (
                <>Hide <ChevronUp size={14} /></>
              ) : (
                <>Show <ChevronDown size={14} /></>
              )}
            </Button>
          </Col>
        </Row>
        
        {asset.issues.length > 0 && (
          <div className="mt-3">
            <h6>Top Issues</h6>
            {asset.issues.slice(0, 2).map((issue, index) => (
              <Alert 
                key={index}
                color={issue.type === 'critical' ? 'danger' : 'warning'}
                className="mb-2"
              >
                <strong>{issue.category.replace(/_/g, ' ')}:</strong> {issue.message}
              </Alert>
            ))}
            {asset.issues.length > 2 && (
              <small className="text-muted">
                +{asset.issues.length - 2} more issues (expand for details)
              </small>
            )}
          </div>
        )}
        
        <Collapse isOpen={expandedAssets[asset.symbol]}>
          <hr />
          
          {/* All Issues */}
          {asset.issues.length > 2 && (
            <div className="mb-4">
              <h6>All Issues ({asset.issues.length})</h6>
              {asset.issues.map((issue, index) => (
                <Alert 
                  key={index}
                  color={issue.type === 'critical' ? 'danger' : 'warning'}
                  className="mb-2"
                >
                  <strong>{issue.category.replace(/_/g, ' ')}:</strong> {issue.message}
                  {issue.source && (
                    <Badge color="secondary" className="ms-2">
                      {issue.source}
                    </Badge>
                  )}
                </Alert>
              ))}
            </div>
          )}
          
          {/* Timeframe Details - Enhanced for AI Prediction Readiness */}
          <h6>AI Prediction Readiness Analysis</h6>
          {Object.values(asset.timeframes).map((tf, index) => (
            <div key={index} className="mb-2">
              <div className="d-flex justify-content-between align-items-center p-2 border rounded">
                <div>
                  <strong>{tf.timeframe || `Timeframe ${index + 1}`}</strong>
                  {tf.continuityAnalysis && (
                    <div>
                      <small className="text-muted">
                        {tf.continuityAnalysis.continuousFromEnd} continuous recent records
                        {tf.continuityAnalysis.dataFreshness && 
                          ` • ${tf.continuityAnalysis.dataFreshness}h ago`
                        }
                      </small>
                    </div>
                  )}
                </div>
                <div className="text-end">
                  {tf.continuityAnalysis?.readyForAI ? (
                    <Badge color="success">AI Ready</Badge>
                  ) : (
                    <Badge color={tf.continuityAnalysis?.continuousFromEnd < 100 ? "danger" : "warning"}>
                      Need {200 - (tf.continuityAnalysis?.continuousFromEnd || 0)} more
                    </Badge>
                  )}
                </div>
              </div>
              {tf.continuityAnalysis && (
                <div className="mt-1">
                  <div className="progress" style={{ height: '4px' }}>
                    <div 
                      className={`progress-bar ${tf.continuityAnalysis.readyForAI ? 'bg-success' : 'bg-warning'}`}
                      style={{ 
                        width: `${Math.min(100, (tf.continuityAnalysis.continuousFromEnd / 200) * 100)}%` 
                      }}
                    ></div>
                  </div>
                  <small className="text-muted">
                    {tf.continuityAnalysis.continuousFromEnd}/200 continuous data points for AI prediction
                  </small>
                </div>
              )}
            </div>
          ))}
          
          {Object.keys(asset.timeframes).length === 0 && (
            <Alert color="warning">
              <i className="fe fe-alert-triangle me-2"></i>
              No timeframe data available for this asset.
            </Alert>
          )}
        </Collapse>
      </CardBody>
    </Card>
  );

  const renderRecommendations = () => (
    <Card>
      <CardHeader>
        <h5 className="mb-0">Recommendations</h5>
      </CardHeader>
      <CardBody>
        {diagnostics.recommendations.map((rec, index) => (
          <Alert 
            key={index}
            color={rec.priority === 'critical' ? 'danger' : rec.priority === 'high' ? 'warning' : 'info'}
            className="mb-3"
          >
            <h6>{rec.action.replace(/_/g, ' ').toUpperCase()}</h6>
            <p className="mb-2">{rec.message}</p>
            {rec.steps && (
              <ul className="mb-0">
                {rec.steps.map((step, stepIndex) => (
                  <li key={stepIndex}>{step}</li>
                ))}
              </ul>
            )}
          </Alert>
        ))}
      </CardBody>
    </Card>
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col>
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                <div>
                  <h4 className="mb-sm-0">Data Quality Diagnostics</h4>
                  <p className="text-muted mb-0">
                    Comprehensive analysis of data integrity and quality across all assets
                  </p>
                </div>
                <div className="page-title-right">
                  <Button
                    color="primary"
                    onClick={runDiagnostics}
                    disabled={loading}
                    className="d-flex align-items-center gap-2"
                  >
                    {loading ? (
                      <>
                        <Spinner size="sm" /> Running Diagnostics...
                      </>
                    ) : (
                      <>
                        <Play size={16} /> Run Diagnostics
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          {loading && (
            <Row>
              <Col>
                <Card>
                  <CardBody className="text-center">
                    <Progress animated value={100} className="mb-3" />
                    <p className="text-muted">
                      Analyzing data quality across all assets and timeframes...
                    </p>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}

          {error && (
            <Row>
              <Col>
                <Alert color="danger">
                  {error}
                </Alert>
              </Col>
            </Row>
          )}

          {diagnostics && (
            <>
              {/* Summary Cards */}
              <Row>
                <Col xl={3} md={6}>
                  <Card className="card-animate">
                    <CardBody>
                      <div className="d-flex align-items-center">
                        <div className="flex-grow-1 overflow-hidden">
                          <p className="text-uppercase fw-medium text-muted text-truncate mb-0">
                            Total Assets
                          </p>
                        </div>
                      </div>
                      <div className="d-flex align-items-end justify-content-between mt-4">
                        <div>
                          <h4 className="fs-22 fw-semibold ff-secondary mb-4">
                            <span className="counter-value">
                              {diagnostics.summary.totalAssets}
                            </span>
                          </h4>
                          <small className="text-muted">
                            {formatNumber(diagnostics.summary.totalRecords)} records
                          </small>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                <Col xl={3} md={6}>
                  <Card className="card-animate">
                    <CardBody>
                      <div className="d-flex align-items-center">
                        <div className="flex-grow-1 overflow-hidden">
                          <p className="text-uppercase fw-medium text-muted text-truncate mb-0">
                            Healthy Assets
                          </p>
                        </div>
                      </div>
                      <div className="d-flex align-items-end justify-content-between mt-4">
                        <div>
                          <h4 className="fs-22 fw-semibold ff-secondary mb-4 text-success">
                            <span className="counter-value">
                              {diagnostics.summary.healthyAssets}
                            </span>
                          </h4>
                          <small className="text-muted">No issues detected</small>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                <Col xl={3} md={6}>
                  <Card className="card-animate">
                    <CardBody>
                      <div className="d-flex align-items-center">
                        <div className="flex-grow-1 overflow-hidden">
                          <p className="text-uppercase fw-medium text-muted text-truncate mb-0">
                            Warnings
                          </p>
                        </div>
                      </div>
                      <div className="d-flex align-items-end justify-content-between mt-4">
                        <div>
                          <h4 className="fs-22 fw-semibold ff-secondary mb-4 text-warning">
                            <span className="counter-value">
                              {diagnostics.summary.warnings}
                            </span>
                          </h4>
                          <small className="text-muted">Minor issues</small>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>

                <Col xl={3} md={6}>
                  <Card className="card-animate">
                    <CardBody>
                      <div className="d-flex align-items-center">
                        <div className="flex-grow-1 overflow-hidden">
                          <p className="text-uppercase fw-medium text-muted text-truncate mb-0">
                            Critical Issues
                          </p>
                        </div>
                      </div>
                      <div className="d-flex align-items-end justify-content-between mt-4">
                        <div>
                          <h4 className="fs-22 fw-semibold ff-secondary mb-4 text-danger">
                            <span className="counter-value">
                              {diagnostics.summary.criticalIssues}
                            </span>
                          </h4>
                          <small className="text-muted">Immediate attention</small>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* AI Prediction Readiness Summary */}
              <Row>
                <Col>
                  <Card>
                    <CardHeader>
                      <h6 className="mb-0">
                        <i className="fe fe-cpu me-2"></i>
                        AI Prediction Readiness Status
                      </h6>
                    </CardHeader>
                    <CardBody>
                      <Row>
                        {(() => {
                          // Calculate AI readiness statistics
                          let assetsReadyForAI = 0;
                          let totalTimeframes = 0;
                          let readyTimeframes = 0;
                          
                          diagnostics.assets.forEach(asset => {
                            let assetReady = true;
                            Object.values(asset.timeframes || {}).forEach(tf => {
                              totalTimeframes++;
                              if (tf.continuityAnalysis?.readyForAI) {
                                readyTimeframes++;
                              } else {
                                assetReady = false;
                              }
                            });
                            if (assetReady && Object.keys(asset.timeframes || {}).length > 0) {
                              assetsReadyForAI++;
                            }
                          });
                          
                          const readyPercentage = totalTimeframes > 0 ? Math.round((readyTimeframes / totalTimeframes) * 100) : 0;
                          
                          return (
                            <>
                              <Col md={4}>
                                <div className="text-center">
                                  <h3 className={`mb-1 ${assetsReadyForAI === diagnostics.summary.totalAssets ? 'text-success' : 'text-warning'}`}>
                                    {assetsReadyForAI}/{diagnostics.summary.totalAssets}
                                  </h3>
                                  <p className="text-muted mb-0">Assets Ready for AI</p>
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className="text-center">
                                  <h3 className={`mb-1 ${readyPercentage >= 75 ? 'text-success' : readyPercentage >= 50 ? 'text-warning' : 'text-danger'}`}>
                                    {readyPercentage}%
                                  </h3>
                                  <p className="text-muted mb-0">Timeframes Ready</p>
                                  <div className="progress mt-2" style={{ height: '6px' }}>
                                    <div 
                                      className={`progress-bar ${readyPercentage >= 75 ? 'bg-success' : readyPercentage >= 50 ? 'bg-warning' : 'bg-danger'}`}
                                      style={{ width: `${readyPercentage}%` }}
                                    ></div>
                                  </div>
                                </div>
                              </Col>
                              <Col md={4}>
                                <div className="text-center">
                                  <h3 className="mb-1 text-info">200</h3>
                                  <p className="text-muted mb-0">Required Data Points</p>
                                  <small className="text-muted">Continuous recent records</small>
                                </div>
                              </Col>
                            </>
                          );
                        })()}
                      </Row>
                      {(() => {
                        // Calculate readiness status message
                        let assetsReadyForAI = 0;
                        diagnostics.assets.forEach(asset => {
                          let assetReady = true;
                          Object.values(asset.timeframes || {}).forEach(tf => {
                            if (!tf.continuityAnalysis?.readyForAI) {
                              assetReady = false;
                            }
                          });
                          if (assetReady && Object.keys(asset.timeframes || {}).length > 0) {
                            assetsReadyForAI++;
                          }
                        });
                        
                        if (assetsReadyForAI === diagnostics.summary.totalAssets) {
                          return (
                            <Alert color="success" className="mt-3">
                              <strong>🚀 AI Prediction Ready!</strong> All assets have sufficient continuous data for machine learning models.
                            </Alert>
                          );
                        } else if (assetsReadyForAI > diagnostics.summary.totalAssets / 2) {
                          return (
                            <Alert color="warning" className="mt-3">
                              <strong>⚡ Partial AI Readiness</strong> {assetsReadyForAI} of {diagnostics.summary.totalAssets} assets ready. Focus on collecting recent continuous data.
                            </Alert>
                          );
                        } else {
                          return (
                            <Alert color="danger" className="mt-3">
                              <strong>⚠️ AI Prediction Blocked</strong> Most assets lack sufficient continuous data. Prioritize recent data collection.
                            </Alert>
                          );
                        }
                      })()}
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* Status Alerts */}
              {diagnostics.summary.criticalIssues > 0 && (
                <Row>
                  <Col>
                    <Alert color="danger">
                      <h6>Critical Data Quality Issues Detected</h6>
                      <p className="mb-0">
                        {diagnostics.summary.criticalIssues} assets have critical issues requiring immediate attention.
                        Common issues include fake prices, unrealistic volumes, and data source contamination.
                      </p>
                    </Alert>
                  </Col>
                </Row>
              )}

              {diagnostics.summary.warnings > 0 && diagnostics.summary.criticalIssues === 0 && (
                <Row>
                  <Col>
                    <Alert color="warning">
                      <h6>Data Quality Warnings</h6>
                      <p className="mb-0">
                        {diagnostics.summary.warnings} assets have warnings including stale data and continuity gaps.
                      </p>
                    </Alert>
                  </Col>
                </Row>
              )}

              {diagnostics.summary.criticalIssues === 0 && diagnostics.summary.warnings === 0 && (
                <Row>
                  <Col>
                    <Alert color="success">
                      <h6>Excellent Data Quality</h6>
                      <p className="mb-0">
                        All assets show healthy data quality with no critical issues or warnings detected.
                      </p>
                    </Alert>
                  </Col>
                </Row>
              )}

              {/* Global Issues */}
              {diagnostics.globalIssues.length > 0 && (
                <Row>
                  <Col>
                    <Card>
                      <CardHeader>
                        <h5 className="mb-0">Global System Analysis</h5>
                      </CardHeader>
                      <CardBody>
                        {diagnostics.globalIssues.map((issue, index) => (
                          <div key={index} className="mb-3">
                            <Alert 
                              color={issue.type === 'critical' ? 'danger' : issue.type === 'warning' ? 'warning' : 'info'}
                              className="mb-2"
                            >
                              <h6>{issue.category.replace(/_/g, ' ').toUpperCase()}</h6>
                              <p className="mb-0">{issue.message}</p>
                            </Alert>
                            {issue.details && Array.isArray(issue.details) && issue.category === 'source_distribution' && (
                              <div className="table-responsive">
                                <Table size="sm">
                                  <thead>
                                    <tr>
                                      <th>Data Source</th>
                                      <th>Records</th>
                                      <th>Date Range</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {issue.details.map((source, idx) => (
                                      <tr key={idx}>
                                        <td>{source.data_source}</td>
                                        <td>{formatNumber(source.record_count)}</td>
                                        <td>
                                          <small>
                                            {formatDate(source.earliest)} - {formatDate(source.latest)}
                                          </small>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </div>
                            )}
                          </div>
                        ))}
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              )}

              {/* Asset Details */}
              <Row>
                <Col>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5>Asset Analysis ({diagnostics.assets.length})</h5>
                  </div>
                  
                  {diagnostics.assets
                    .sort((a, b) => {
                      const statusOrder = { critical: 0, warning: 1, healthy: 2 };
                      return statusOrder[a.status] - statusOrder[b.status];
                    })
                    .map(renderAssetSummary)}
                </Col>
              </Row>

              {/* Recommendations */}
              {diagnostics.recommendations.length > 0 && (
                <Row>
                  <Col>
                    {renderRecommendations()}
                  </Col>
                </Row>
              )}

              {/* Footer */}
              <Row>
                <Col>
                  <div className="text-center mt-4">
                    <small className="text-muted">
                      Last check: {formatDate(diagnostics.summary.timestamp)}
                    </small>
                  </div>
                </Col>
              </Row>
            </>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default DataQuality;
